import * as React from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { EnvelopeSearchQuery } from '@lib/input/envelope.input'

/**
 * Envelope-list filters that mean the same thing in any organization and mode.
 * Pagination (`limit`, `offset`) belongs to the old result set, so a switch drops it.
 * Every remaining search key has to be listed: a new filter fails the build until
 * someone decides whether switching organization or mode should keep it.
 */
type PortableEnvelopeFilter = Exclude<keyof EnvelopeSearchQuery, 'limit' | 'offset'>

const PORTABLE_ENVELOPE_FILTERS = {
  status: true,
  q: true,
  email: true,
  metadataKey: true,
  metadataValue: true,
} as const satisfies Record<PortableEnvelopeFilter, true>

function isPortableEnvelopeFilter(name: string): name is PortableEnvelopeFilter {
  return Object.keys(PORTABLE_ENVELOPE_FILTERS).some((filter) => filter === name)
}

/**
 * Wraps a switch of organization or mode so it first drops what the URL holds
 * for the old one: a page cursor (`before`), an open record (`delivery`,
 * `entry`, `request`, `message`), a filter by one envelope's id. The URL
 * changes before the switch does, so the next scope is never asked for them.
 */
export function useLeaveScope(): <T>(switchTo: (next: T) => void) => (next: T) => void {
  const navigate = useNavigate()
  const location = useLocation()
  // Read when the switch happens, so the wrapped setter keeps one identity across URL changes.
  const locationRef = React.useRef(location)
  React.useEffect(() => {
    locationRef.current = location
  }, [location])
  return React.useCallback(
    <T>(switchTo: (next: T) => void) =>
      (next: T) => {
        const { pathname, search } = locationRef.current
        const params = new URLSearchParams(search)
        const kept = new URLSearchParams(
          [...params].filter((pair): pair is [PortableEnvelopeFilter, string] => isPortableEnvelopeFilter(pair[0])),
        )
        if (kept.toString() === params.toString()) {
          switchTo(next)
          return
        }
        void Promise.resolve(navigate({ pathname, search: kept.toString() }, { replace: true })).then(() => switchTo(next))
      },
    [navigate],
  )
}
